import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from "rxjs";
import { TranslateService } from '@ngx-translate/core';
import { CustomHttpParams } from '../shared/loader/custom-httpParam';
import { FirmwareFamily } from '../device/device-management.service';

@Injectable()
export class AdminService {

	constructor(
		private httpClient: HttpClient,
		private translate: TranslateService
	) { }

	getLoggingInfo(options) {
		return this.httpClient.post('/admin/getLoggingInfo', options, {
			observe: "body"
		});
	}

	deleteLog(id, type) {
		return this.httpClient.delete('/admin/deleteLog', {
			body: {id, type},
			observe: "body"
		});
	}

	getErrorLog(errorId: number, errorType: string) {
		return this.httpClient.get('/admin/getErrorInfo', {
			params: { id: errorId.toString(), logType: errorType },
			observe: "body"
		});
	}

	getCronJobsStatus() {
		return this.httpClient.get('/admin/getCronJobsStatus', {
			observe: "body"
		});
	}

	getSchedFunctionsStatus() {
		return this.httpClient.get('/admin/getSchedFunctionsStatus', {
			observe: "body"
		});
	}

	updateSchedFunctionsStatus(schedFunctions) {
		return this.httpClient.patch('/admin/updateSchedFunctionsStatus', schedFunctions, {
			observe: "body"
		});
	}

	getPermission(permissionId, options?) {
		return this.httpClient.post('/admin/getPermission', {permissionId, options}, {
			observe: "body"
		});
	}

	updatePermission(permissionId, permission) {
		return this.httpClient.put('/admin/updatePermission', {permissionId, permission}, {
			observe: "body"
		});
	}

	getRejectedDeviceConnections(options) {
		return this.httpClient.post('/admin/getRejectedDeviceConnections', options, {
			observe: "body"
		});
	}

	getUnusedLogs() {
		return this.httpClient.get('/admin/getUnusedLogs');
	}

	deleteRejectionLog(id) {
		return this.httpClient.delete('/admin/deleteRejectionLog', {
			body: {id},
			observe: "body"
		});
	}

	getActionLogs(options) {
		return this.httpClient.post('/admin/getActionLogs', options, {
			observe: "body"
		});
	}

	getContactUs(options?) {
		return this.httpClient.get('/admin/getContactUs', {
			params: options || {},
			observe: "body"
		});
	}

	deleteContactUs(ids) {
		return this.httpClient.delete('/admin/deleteContactUs', {
			body: {ids},
			observe: "body"
		});
	}

	getUsersForAudit() {
		return this.httpClient.get('/admin/getUsersForAudit', {
			observe: "body"
		});
	}

	getPermissionsForAudit() {
		return this.httpClient.get('/admin/getPermissionsForAudit', {
			observe: "body"
		});
	}

	getRegistrationRequests(id?) {
		const params = id ? { id } : {};
		return this.httpClient.get('/user/getRegistrationRequests', {
			params,
			observe: "body"
		});
	}

	submitRegistrationRequest(registrationRequest) {
		return this.httpClient.post('/user/submitRegistrationRequest', {registrationRequest}, {
			observe: "body"
		});
	}

	forceWebsiteRefresh() {
		return this.httpClient.post('/admin/forceWebsiteRefresh', {}, {
			observe: "body"
		});
	}

	addSchedToRun(sched: any) {
		return this.httpClient.post('/admin/addSchedToRun', { sched	}, {
			observe: "body"
		});
	}

	uploadFWFile(data) {
		const headers = new HttpHeaders();
		headers.append('Content-Type', 'multipart/form-data');

		return this.httpClient.post('/upload/uploadFWFile', data, {
			observe: "body",
			headers,
		});
	}

	updateFWNote(version: string | number, note: string, deviceFamily: FirmwareFamily = FirmwareFamily.IOTAH) {
		return this.httpClient.patch('/admin/updateFWNote', { version, note, deviceFamily }, {
			observe: "body"
		});
	}

	getAllSims() {
		return this.httpClient.get('/cellular/getAllSims', {
			observe: "body"
		})
	}

	getConnectivityVerificationLogs(reportId: number, fromMonth?: string, toMonth?: string, isImDevice?: boolean, deviceFamily?: string) {
		return this.httpClient.get('/admin/getConnectivityVerificationLogs', {
			observe: "body",
			params: {
				reportId,
				fromMonth,
				toMonth,
				...(isImDevice ? { isImDevice: 'true' } : {}),
				...(deviceFamily ? { deviceFamily } : {})
			}
		});
	}

	generateData(options: {deviceId: string, from: Date, to: Date, type: string, maxPerDay: number, withDailyDetails: boolean, withAnalytics: boolean}) {
		return this.httpClient.post('/admin/generateData', { options }, {
			observe: "body"
		})
	}

	getConnectivityVerificationStatistics() {
		return this.httpClient.get('/admin/getConnectivityVerificationStatistics', {
			observe: 'body'
		});
	}

	getHardwareErrorsStatistics() {
		return this.httpClient.get('/admin/getHardwareErrorsStatistics', {
			observe: 'body'
		});
	}

	getDebugLogs(startLine=0, fileName=null) {
		const params: any = { startLine: startLine.toString() };
		if (fileName) {
			params.fileName = fileName;
		}
		return this.httpClient.get('/admin/getDebugLogs', {
			params,
			observe: "body",
			context: new CustomHttpParams({ noUIBlock: true }) as any
		});
	}

	generateScript(command, macAddress, siteId, fileName, excludeLargeSites, deviceTypes = []) {
		return this.httpClient.post('/admin/generateScript', { command, macAddress, siteId, fileName, excludeLargeSites, deviceTypes }, {
			observe: "body"
		});
	}

	getAnalyticsSource() {
		return this.httpClient.get('/admin/getAnalyticsSource', {
			observe: 'body'
		});
	}

	setAnalyticsSource(source: 'old' | 'new') {
		return this.httpClient.post('/admin/setAnalyticsSource', { source }, {
			observe: 'body'
		});
	}

	getEstimatedLocationFlag() {
		return this.httpClient.get('/admin/getEstimatedLocationFlag', {
			observe: 'body'
		});
	}

	setEstimatedLocationFlag(mode: string, macAddresses: string) {
		return this.httpClient.post('/admin/setEstimatedLocationFlag', { mode, macAddresses }, {
			observe: 'body'
		});
	}

	getFwUpdateThrottleLimit() {
		return this.httpClient.get('/admin/getFwUpdateThrottleLimit', {
			observe: 'body'
		});
	}

	setFwUpdateThrottleLimit(limit: number) {
		return this.httpClient.post('/admin/setFwUpdateThrottleLimit', { limit }, {
			observe: 'body'
		});
	}

	getCurrentAppVersionInfo() {
		return this.httpClient.get('/admin/getCurrentAppVersionInfo', {
			observe: "body"
		});
	}

	saveAppVersionSettings(appVersionSettings: { minium_app_version: string, latest_app_version: string, st_min_app_version: string, st_latest_app_version: string,  userId: number }) {
		return this.httpClient.put('/admin/saveAppVersionSettings', { appVersionSettings }, {
			observe: "body"
		});
	}

	getPasswordRevealRequests(filters: { status?: string, request_type?: string, limit?: number, offset?: number }) {
		return this.httpClient.get('/admin/password-reveal-requests/list', {
			params: filters as any,
			observe: "body"
		});
	}

	approvePasswordRevealRequest(requestId: number) {
		return this.httpClient.post(`/admin/password-reveal-requests/${requestId}/approve`, {}, {
			observe: "body"
		});
	}

	rejectPasswordRevealRequest(requestId: number, rejectReason: string) {
		return this.httpClient.post(`/admin/password-reveal-requests/${requestId}/reject`, { reject_reason: rejectReason }, {
			observe: "body"
		});
	}

	getDemoSitePairs() {
		return this.httpClient.get('/admin/getDemoSitePairs', {
			observe: "body"
		});
	}

	saveDemoSitePair(sourceSiteId: number, targetSiteId: number) {
		return this.httpClient.post('/admin/saveDemoSitePair', { sourceSiteId, targetSiteId }, {
			observe: "body"
		});
	}

	deleteDemoSitePair(id: number) {
		return this.httpClient.delete('/admin/deleteDemoSitePair', {
			body: { id },
			observe: "body"
		});
	}

	requestDemoSitePairRefresh(id: number) {
		return this.httpClient.post('/admin/requestDemoSitePairRefresh', { id }, {
			observe: "body"
		});
	}

	requestDemoSitePairRemoval(id: number) {
		return this.httpClient.post('/admin/requestDemoSitePairRemoval', { id }, {
			observe: "body"
		});
	}
}
